import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  Anchor, LayoutDashboard, Calendar, Package, DollarSign, Users, Settings,
  RefreshCw, Plus, Edit, Trash2, Menu, X, LogOut, QrCode, Download,
  TrendingUp, Clock, Check, CreditCard, Banknote, Shield, AlertTriangle, Eye, EyeOff, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api, useAuth } from '../App';

const OwnerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/owner', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/owner/tours', icon: Package, label: 'Tours' },
    { path: '/owner/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/owner/referrals', icon: Users, label: 'Referrals' },
    { path: '/owner/earnings', icon: DollarSign, label: 'Earnings' },
    { path: '/owner/staff', icon: Users, label: 'Staff' },
    { path: '/owner/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border">
        <div className="p-6 border-b border-border">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-10 w-auto" />
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label, exact }) => {
            const isActive = exact ? location.pathname === path : location.pathname.startsWith(path);
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-primary/10 text-primary font-medium' 
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold">{user?.name?.[0] || 'O'}</span>
            </div>
            <div>
              <p className="font-medium text-sm">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Link to="/change-password" className="block">
            <Button variant="ghost" className="w-full justify-start text-muted-foreground">
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-border z-50">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="The Village at Athol Island" className="h-8 w-auto" />
          </Link>
          <div className="w-10" />
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <span className="font-serif font-bold">Menu</span>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {navItems.map(({ path, icon: Icon, label, exact }) => {
                const isActive = exact ? location.pathname === path : location.pathname.startsWith(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-primary/10 text-primary font-medium' 
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:pt-0 pt-16">
        <Routes>
          <Route index element={<OwnerOverview />} />
          <Route path="tours" element={<ToursManagement />} />
          <Route path="bookings" element={<BookingsManagement />} />
          <Route path="referrals" element={<ReferralsManagement />} />
          <Route path="earnings" element={<EarningsReport />} />
          <Route path="staff" element={<StaffManagement />} />
          <Route path="settings" element={<PaymentSettings />} />
        </Routes>
      </main>
    </div>
  );
};

// Owner Overview
const OwnerOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await api.get('/dashboard/owner');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl lg:text-3xl font-bold" data-testid="owner-dashboard-title">Owner Dashboard</h1>
          <p className="text-muted-foreground">Business overview and management</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="w-12 h-12 rounded-full bg-teal-light text-primary flex items-center justify-center mb-3">
            <Package className="w-6 h-6" />
          </div>
          <p className="text-2xl font-bold">{data?.tours?.total || 0}</p>
          <p className="text-sm text-muted-foreground">Active Tour</p>
        </div>
        
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="w-12 h-12 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center mb-3">
            <Calendar className="w-6 h-6" />
          </div>
          <p className="text-2xl font-bold">{data?.bookings?.total || 0}</p>
          <p className="text-sm text-muted-foreground">Total Bookings</p>
        </div>
        
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-3">
            <DollarSign className="w-6 h-6" />
          </div>
          <p className="text-2xl font-bold">${(data?.earnings?.total_collected || 0).toFixed(2)}</p>
          <p className="text-sm text-muted-foreground">Total Revenue</p>
        </div>
        
        <div className="bg-white rounded-xl border border-border p-5">
          <div className="w-12 h-12 rounded-full bg-coral-light text-coral-dark flex items-center justify-center mb-3">
            <Users className="w-6 h-6" />
          </div>
          <p className="text-2xl font-bold">{data?.referrals?.total_referrals || 0}</p>
          <p className="text-sm text-muted-foreground">Referrals</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link to="/owner/tours" className="bg-white rounded-xl border border-border p-6 hover:border-primary/50 transition-colors">
          <Package className="w-8 h-8 text-primary mb-3" />
          <h3 className="font-serif font-bold text-lg mb-1">Manage Tour</h3>
          <p className="text-sm text-muted-foreground">Create, edit, and manage your tour offerings</p>
        </Link>
        
        <Link to="/owner/referrals" className="bg-white rounded-xl border border-border p-6 hover:border-primary/50 transition-colors">
          <Users className="w-8 h-8 text-primary mb-3" />
          <h3 className="font-serif font-bold text-lg mb-1">Referral Partners</h3>
          <p className="text-sm text-muted-foreground">Manage taxi drivers, hotels, and partners</p>
        </Link>
        
        <Link to="/owner/earnings" className="bg-white rounded-xl border border-border p-6 hover:border-primary/50 transition-colors">
          <TrendingUp className="w-8 h-8 text-primary mb-3" />
          <h3 className="font-serif font-bold text-lg mb-1">View Earnings</h3>
          <p className="text-sm text-muted-foreground">Track revenue, deposits, and payouts</p>
        </Link>
      </div>
    </div>
  );
};

// Tour Management
const ToursManagement = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTour, setEditingTour] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [editForm, setEditForm] = useState({
    title: '', description: '', base_price: '', child_price: '', 
    duration_hours: '', meeting_point: '', capacity: '', images: []
  });

  const fetchTours = async () => {
    try {
      const response = await api.get('/tours');
      setTours(response.data);
    } catch (error) {
      console.error('Error fetching tours:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTours(); }, []);

  const openEdit = (tour) => {
    setEditingTour(tour);
    setEditForm({
      title: tour.title || '',
      description: tour.description || '',
      base_price: tour.base_price?.toString() || '',
      child_price: tour.child_price?.toString() || '',
      duration_hours: tour.duration_hours?.toString() || '',
      meeting_point: tour.meeting_point || '',
      capacity: tour.capacity?.toString() || '',
      images: tour.images || []
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditForm(prev => ({ ...prev, images: [...prev.images, res.data.url] }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    setEditForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (editForm.title) updateData.title = editForm.title;
      if (editForm.description) updateData.description = editForm.description;
      if (editForm.base_price) updateData.base_price = parseFloat(editForm.base_price);
      if (editForm.child_price) updateData.child_price = parseFloat(editForm.child_price);
      if (editForm.duration_hours) updateData.duration_hours = parseFloat(editForm.duration_hours);
      if (editForm.meeting_point) updateData.meeting_point = editForm.meeting_point;
      if (editForm.capacity) updateData.capacity = parseInt(editForm.capacity);
      updateData.images = editForm.images;

      await api.put(`/tours/${editingTour.id}`, updateData);
      toast.success('Tour updated successfully');
      setEditingTour(null);
      fetchTours();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl font-bold">Tour Management</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tours.map((tour) => (
          <div key={tour.id} className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="h-32 overflow-hidden">
              <img 
                src={tour.images?.[0] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400'}
                alt={tour.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="font-serif font-bold mb-1">{tour.title}</h3>
              <p className="text-sm text-muted-foreground mb-3">{tour.category}</p>
              <div className="flex items-center justify-between">
                <span className="font-bold text-primary">BSD ${tour.base_price}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(tour)} data-testid={`edit-tour-${tour.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Tour Modal */}
      {editingTour && (
        <Dialog open={!!editingTour} onOpenChange={(open) => !open && setEditingTour(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Edit Tour</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">
              {/* Images */}
              <div>
                <Label className="mb-2 block font-semibold">Tour Images</Label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {editForm.images.map((img, idx) => (
                    <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                    {uploading ? (
                      <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Plus className="w-5 h-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground mt-1">Upload</span>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label>Tour Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1"
                  data-testid="edit-tour-title"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description</Label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 w-full min-h-[120px] rounded-md border border-border px-3 py-2 text-sm resize-y"
                  data-testid="edit-tour-description"
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Adult Price (BSD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.base_price}
                    onChange={(e) => setEditForm(prev => ({ ...prev, base_price: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-tour-price"
                  />
                </div>
                <div>
                  <Label>Child Price (BSD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.child_price}
                    onChange={(e) => setEditForm(prev => ({ ...prev, child_price: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-tour-child-price"
                  />
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editForm.duration_hours}
                    onChange={(e) => setEditForm(prev => ({ ...prev, duration_hours: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Meeting Point</Label>
                  <Input
                    value={editForm.meeting_point}
                    onChange={(e) => setEditForm(prev => ({ ...prev, meeting_point: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={editForm.capacity}
                    onChange={(e) => setEditForm(prev => ({ ...prev, capacity: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-border">
                <Button className="btn-primary flex-1" onClick={handleSave} disabled={saving} data-testid="save-tour-btn">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditingTour(null)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Bookings Management
const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await api.get('/bookings');
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await api.put(`/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}`);
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, status: newStatus } : b
      ));
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <h2 className="font-serif text-2xl font-bold mb-6">Bookings Management</h2>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 font-semibold text-sm">Customer</th>
                <th className="text-left p-4 font-semibold text-sm">Tour</th>
                <th className="text-left p-4 font-semibold text-sm">Date</th>
                <th className="text-left p-4 font-semibold text-sm">Status</th>
                <th className="text-left p-4 font-semibold text-sm">Payment</th>
                <th className="text-left p-4 font-semibold text-sm">Amount</th>
                <th className="text-left p-4 font-semibold text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <p className="font-medium">{booking.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{booking.customer_email}</p>
                  </td>
                  <td className="p-4">{booking.tour_title}</td>
                  <td className="p-4">
                    <p>{booking.date}</p>
                    <p className="text-sm text-muted-foreground">{booking.time_slot}</p>
                  </td>
                  <td className="p-4">
                    <Select 
                      value={booking.status} 
                      onValueChange={(value) => handleStatusChange(booking.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      booking.payment_status === 'fully_paid' ? 'badge-confirmed' :
                      booking.payment_status === 'pay_on_arrival' ? 'bg-amber-100 text-amber-800' :
                      booking.payment_status === 'deposit_paid' ? 'badge-pending' : 'badge-cancelled'
                    }`} data-testid={`payment-status-${booking.id}`}>
                      {booking.payment_status === 'pay_on_arrival' ? 'Pay on Arrival' : booking.payment_status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-4 font-semibold">${booking.total_amount}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {booking.payment_status === 'pay_on_arrival' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleMarkAsPaid(booking.id)}
                          data-testid={`mark-paid-${booking.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                      <Button size="sm" variant="ghost">
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Referrals Management
const ReferralsManagement = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newPartner, setNewPartner] = useState({
    name: '',
    partner_type: 'taxi',
    email: '',
    phone: '',
    commission_percentage: 10
  });

  const fetchPartners = async () => {
    try {
      const response = await api.get('/referrals/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleAddPartner = async () => {
    try {
      await api.post('/referrals/partners', newPartner);
      toast.success('Partner added successfully');
      setShowAddDialog(false);
      setNewPartner({ name: '', partner_type: 'taxi', email: '', phone: '', commission_percentage: 10 });
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add partner');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold">Referral Partners</h2>
          <p className="text-muted-foreground">Manage taxi drivers, hotels, and other referral partners</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-partner-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Referral Partner</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Partner Name</Label>
                <Input
                  value={newPartner.name}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Island Taxi Co"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select 
                  value={newPartner.partner_type} 
                  onValueChange={(value) => setNewPartner(prev => ({ ...prev, partner_type: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taxi">Taxi Driver</SelectItem>
                    <SelectItem value="hotel">Hotel</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newPartner.email}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="partner@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone (Optional)</Label>
                <Input
                  value={newPartner.phone}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (242) 555-0123"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Commission %</Label>
                <Input
                  type="number"
                  value={newPartner.commission_percentage}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, commission_percentage: parseFloat(e.target.value) }))}
                  className="mt-1"
                />
              </div>
              <Button className="w-full btn-primary" onClick={handleAddPartner}>
                Add Partner
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {partners.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-border">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-serif font-bold text-lg mb-2">No Partners Yet</h3>
          <p className="text-muted-foreground mb-4">Add taxi drivers or hotels to start your referral program</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((partner) => (
            <div key={partner.id} className="bg-white rounded-xl border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-serif font-bold text-lg">{partner.name}</h3>
                  <span className="text-sm text-muted-foreground capitalize">{partner.partner_type}</span>
                </div>
                <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  {partner.commission_percentage}% commission
                </span>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <p className="text-muted-foreground">{partner.email}</p>
                {partner.phone && <p className="text-muted-foreground">{partner.phone}</p>}
              </div>

              <div className="flex gap-4 text-sm mb-4">
                <div>
                  <p className="font-bold text-lg">{partner.total_referrals}</p>
                  <p className="text-muted-foreground">Referrals</p>
                </div>
                <div>
                  <p className="font-bold text-lg text-green-600">${partner.total_earnings.toFixed(2)}</p>
                  <p className="text-muted-foreground">Earned</p>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm text-muted-foreground mb-2">Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono">
                    {partner.referral_code}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(partner.referral_code);
                    toast.success('Copied!');
                  }}>
                    Copy
                  </Button>
                </div>
              </div>

              {partner.qr_code_data && (
                <div className="mt-4 flex justify-center">
                  <img 
                    src={`data:image/png;base64,${partner.qr_code_data}`}
                    alt="QR Code"
                    className="w-24 h-24"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Earnings Report
const EarningsReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/dashboard/owner');
        setData(response.data);
      } catch (error) {
        console.error('Error fetching earnings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <h2 className="font-serif text-2xl font-bold mb-6">Earnings Report</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-muted-foreground">Total Collected</span>
          </div>
          <p className="text-3xl font-bold text-green-600">
            ${(data?.earnings?.total_collected || 0).toFixed(2)}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-muted-foreground">Pending Balances</span>
          </div>
          <p className="text-3xl font-bold text-yellow-600">
            ${(data?.earnings?.by_payment_status?.deposit_paid || 0).toFixed(2)}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-coral-dark" />
            <span className="text-muted-foreground">Referral Commissions</span>
          </div>
          <p className="text-3xl font-bold text-coral-dark">
            ${(data?.referrals?.total_commissions || 0).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border p-6">
        <h3 className="font-serif font-bold text-lg mb-4">Earnings by Payment Status</h3>
        <div className="space-y-4">
          {Object.entries(data?.earnings?.by_payment_status || {}).map(([status, amount]) => (
            <div key={status} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="capitalize">{status.replace('_', ' ')}</span>
              <span className="font-bold">${amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// Staff Management
const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', password: '', role: 'operator' });
  const [saving, setSaving] = useState(false);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff');
      setStaff(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleAdd = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    try {
      await api.post('/staff', newStaff);
      toast.success(`Staff member ${newStaff.name} added`);
      setShowAdd(false);
      setNewStaff({ name: '', email: '', password: '', role: 'operator' });
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from staff?`)) return;
    try {
      await api.delete(`/staff/${id}`);
      toast.success(`${name} removed`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-48"><div className="spinner" /></div>;

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold" data-testid="staff-title">Staff Management</h2>
          <p className="text-muted-foreground text-sm">Manage staff accounts with access to the system</p>
        </div>
        <Button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="add-staff-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left p-4 text-sm font-semibold">Name</th>
              <th className="text-left p-4 text-sm font-semibold">Email</th>
              <th className="text-left p-4 text-sm font-semibold">Role</th>
              <th className="text-left p-4 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-b border-border last:border-0">
                <td className="p-4 font-medium">{s.name}</td>
                <td className="p-4 text-sm text-muted-foreground">{s.email}</td>
                <td className="p-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    s.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {s.role.charAt(0).toUpperCase() + s.role.slice(1)}
                  </span>
                </td>
                <td className="p-4">
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(s.id, s.name)} data-testid={`delete-staff-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {staff.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">No staff members yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Staff Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name</Label>
              <Input value={newStaff.name} onChange={(e) => setNewStaff(prev => ({ ...prev, name: e.target.value }))} className="mt-1" data-testid="staff-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={newStaff.email} onChange={(e) => setNewStaff(prev => ({ ...prev, email: e.target.value }))} className="mt-1" data-testid="staff-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={newStaff.password} onChange={(e) => setNewStaff(prev => ({ ...prev, password: e.target.value }))} className="mt-1" placeholder="Min 6 characters" data-testid="staff-password" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newStaff.role} onValueChange={(v) => setNewStaff(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator (Check-in & Bookings)</SelectItem>
                  <SelectItem value="owner">Owner (Full Access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full btn-primary" onClick={handleAdd} disabled={saving} data-testid="save-staff-btn">
              {saving ? 'Adding...' : 'Add Staff Member'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Payment Settings
const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMode, setSelectedMode] = useState('cash_on_arrival');
  const [showPlugnPayKey, setShowPlugnPayKey] = useState(false);
  const [showCybFields, setShowCybFields] = useState(false);

  const [plugnpayCreds, setPlugnpayCreds] = useState({ merchant_id: '', api_key: '' });
  const [cybersourceCreds, setCybersourceCreds] = useState({ merchant_id: '', api_key: '', secret_key: '', environment: 'sandbox' });

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/payment');
      setSettings(response.data);
      setSelectedMode(response.data.payment_mode);
      if (response.data.plugnpay?.merchant_id) {
        setPlugnpayCreds(prev => ({ ...prev, merchant_id: response.data.plugnpay.merchant_id }));
      }
      if (response.data.cybersource?.merchant_id) {
        setCybersourceCreds(prev => ({
          ...prev,
          merchant_id: response.data.cybersource.merchant_id,
          environment: response.data.cybersource.environment || 'sandbox'
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSaveCredentials = async (gateway) => {
    setSaving(true);
    try {
      const payload = {
        payment_mode: selectedMode,
        ...(gateway === 'plugnpay' && { plugnpay: plugnpayCreds }),
        ...(gateway === 'cybersource' && { cybersource: cybersourceCreds })
      };
      await api.put('/settings/payment', payload);
      toast.success(`${gateway === 'plugnpay' ? "RBC Approved Payment Processor PNP" : 'Visa Payment Processor'} credentials saved`);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleModeSwitch = async (mode) => {
    setSaving(true);
    try {
      await api.put('/settings/payment', { payment_mode: mode });
      setSelectedMode(mode);
      toast.success(`Payment mode switched to ${mode === 'cash_on_arrival' ? 'Cash on Arrival' : mode === 'plugnpay' ? "RBC Approved Payment Processor PNP" : 'Visa Payment Processor'}`);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch payment mode');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h2 className="font-serif text-2xl font-bold" data-testid="settings-title">Payment Settings</h2>
        <p className="text-muted-foreground">Configure how customers pay for bookings</p>
      </div>

      {/* Active Payment Mode */}
      <div className="mb-8">
        <h3 className="font-semibold text-lg mb-4">Active Payment Mode</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cash on Arrival */}
          <button
            onClick={() => handleModeSwitch('cash_on_arrival')}
            disabled={saving}
            className={`relative p-5 rounded-xl border-2 text-left transition-all ${
              selectedMode === 'cash_on_arrival'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/30'
            }`}
            data-testid="mode-cash-on-arrival"
          >
            {selectedMode === 'cash_on_arrival' && (
              <div className="absolute top-3 right-3">
                <Check className="w-5 h-5 text-primary" />
              </div>
            )}
            <Banknote className="w-8 h-8 text-amber-600 mb-3" />
            <h4 className="font-semibold mb-1">Cash on Arrival</h4>
            <p className="text-sm text-muted-foreground">Customers book online, pay cash at check-in</p>
          </button>

          {/* RBC Approved PNP */}
          <button
            onClick={() => handleModeSwitch('plugnpay')}
            disabled={saving || !settings?.plugnpay?.configured}
            className={`relative p-5 rounded-xl border-2 text-left transition-all ${
              selectedMode === 'plugnpay'
                ? 'border-primary bg-primary/5'
                : settings?.plugnpay?.configured
                  ? 'border-border hover:border-primary/30'
                  : 'border-border opacity-60 cursor-not-allowed'
            }`}
            data-testid="mode-plugnpay"
          >
            {selectedMode === 'plugnpay' && (
              <div className="absolute top-3 right-3">
                <Check className="w-5 h-5 text-primary" />
              </div>
            )}
            <CreditCard className="w-8 h-8 text-blue-600 mb-3" />
            <h4 className="font-semibold mb-1">RBC Approved PNP</h4>
            <p className="text-sm text-muted-foreground">Accept credit / debit cards via RBC Payment Processor</p>
            {!settings?.plugnpay?.configured && (
              <span className="text-xs text-amber-600 mt-2 block">Credentials required</span>
            )}
          </button>

          {/* Visa Payment Processor */}
          <button
            onClick={() => handleModeSwitch('cybersource')}
            disabled={saving || !settings?.cybersource?.configured}
            className={`relative p-5 rounded-xl border-2 text-left transition-all ${
              selectedMode === 'cybersource'
                ? 'border-primary bg-primary/5'
                : settings?.cybersource?.configured
                  ? 'border-border hover:border-primary/30'
                  : 'border-border opacity-60 cursor-not-allowed'
            }`}
            data-testid="mode-cybersource"
          >
            {selectedMode === 'cybersource' && (
              <div className="absolute top-3 right-3">
                <Check className="w-5 h-5 text-primary" />
              </div>
            )}
            <Shield className="w-8 h-8 text-indigo-600 mb-3" />
            <h4 className="font-semibold mb-1">Visa / Cybersource</h4>
            <p className="text-sm text-muted-foreground">Accept Visa, Mastercard via Cybersource</p>
            {!settings?.cybersource?.configured && (
              <span className="text-xs text-amber-600 mt-2 block">Credentials required</span>
            )}
          </button>
        </div>
      </div>

      {/* Gateway Credentials */}
      <div className="space-y-6">
        {/* RBC Approved PNP Credentials */}
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-lg">RBC Approved Payment Processor PNP</h3>
                <p className="text-sm text-muted-foreground">Credit / debit card processing</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              settings?.plugnpay?.configured
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`} data-testid="plugnpay-status">
              {settings?.plugnpay?.configured ? 'Configured' : 'Not Configured'}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Merchant ID</Label>
              <Input
                value={plugnpayCreds.merchant_id}
                onChange={(e) => setPlugnpayCreds(prev => ({ ...prev, merchant_id: e.target.value }))}
                placeholder="Enter your PNP Merchant ID"
                className="mt-1"
                data-testid="plugnpay-merchant-id"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <div className="relative mt-1">
                <Input
                  type={showPlugnPayKey ? 'text' : 'password'}
                  value={plugnpayCreds.api_key}
                  onChange={(e) => setPlugnpayCreds(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your PNP API Key"
                  data-testid="plugnpay-api-key"
                />
                <button
                  type="button"
                  onClick={() => setShowPlugnPayKey(!showPlugnPayKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPlugnPayKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              onClick={() => handleSaveCredentials('plugnpay')}
              disabled={saving || !plugnpayCreds.merchant_id || !plugnpayCreds.api_key}
              className="btn-primary"
              data-testid="save-plugnpay-btn"
            >
              {saving ? 'Saving...' : 'Save PNP Credentials'}
            </Button>
          </div>
        </div>

        {/* Visa Payment Processor Credentials */}
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-indigo-600" />
              <div>
                <h3 className="font-semibold text-lg">Visa Payment Processor</h3>
                <p className="text-sm text-muted-foreground">Visa, Mastercard, and more</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              settings?.cybersource?.configured
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`} data-testid="cybersource-status">
              {settings?.cybersource?.configured ? 'Configured' : 'Not Configured'}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Merchant ID</Label>
              <Input
                value={cybersourceCreds.merchant_id}
                onChange={(e) => setCybersourceCreds(prev => ({ ...prev, merchant_id: e.target.value }))}
                placeholder="Enter your Visa Processor Merchant ID"
                className="mt-1"
                data-testid="cybersource-merchant-id"
              />
            </div>
            <div>
              <Label>API Key</Label>
              <div className="relative mt-1">
                <Input
                  type={showCybFields ? 'text' : 'password'}
                  value={cybersourceCreds.api_key}
                  onChange={(e) => setCybersourceCreds(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your Visa Processor API Key"
                  data-testid="cybersource-api-key"
                />
                <button
                  type="button"
                  onClick={() => setShowCybFields(!showCybFields)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showCybFields ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Secret Key</Label>
              <Input
                type={showCybFields ? 'text' : 'password'}
                value={cybersourceCreds.secret_key}
                onChange={(e) => setCybersourceCreds(prev => ({ ...prev, secret_key: e.target.value }))}
                placeholder="Enter your Visa Processor Secret Key"
                className="mt-1"
                data-testid="cybersource-secret-key"
              />
            </div>
            <div>
              <Label>Environment</Label>
              <Select
                value={cybersourceCreds.environment}
                onValueChange={(value) => setCybersourceCreds(prev => ({ ...prev, environment: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="cybersource-environment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                  <SelectItem value="production">Production (Live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => handleSaveCredentials('cybersource')}
              disabled={saving || !cybersourceCreds.merchant_id || !cybersourceCreds.api_key || !cybersourceCreds.secret_key}
              className="btn-primary"
              data-testid="save-cybersource-btn"
            >
              {saving ? 'Saving...' : 'Save Visa Processor Credentials'}
            </Button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="settings-info">
        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold mb-1">Important</p>
          <p>You must save valid credentials for a payment processor before you can activate it. Switching to Cash on Arrival can be done at any time. When card payments are active, the checkout page will display a secure card form instead of the cash notice.</p>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
